/**
 * useOrganisations — ViewModel for /manage/organisations.
 *
 * Per SOP 02 §"MVVM Pattern" + §"List Pagination". Composes
 * usePaginatedList for reads; owns CRUD mutations. Each row carries
 * its full address list inline (no separate fetch).
 */

import { useCallback } from 'react'
import { useAuth } from '../context/AuthContext'
import { APIROUTES, fillPath } from '../constants/routes.constants'
import type {
  OrganisationAddressInput,
  OrganisationDto
} from '../models/organisation.model'
import { mutateJson } from '../utils/api/mutateJson'
import { parseApiError } from '../utils/api/parseApiError'
import { usePaginatedList } from './usePaginatedList'

export type {
  OrganisationDto,
  OrganisationAddressDto,
  OrganisationAddressInput
} from '../models/organisation.model'

export interface CreateOrganisationInput {
  readonly name: string
  readonly company_url: string | null
  readonly description: string | null
  readonly addresses: ReadonlyArray<OrganisationAddressInput>
}

export interface UpdateOrganisationInput {
  readonly name?: string
  readonly company_url?: string | null
  readonly description?: string | null
  readonly addresses?: ReadonlyArray<OrganisationAddressInput>
}

export interface UseOrganisationsReturn {
  readonly items: OrganisationDto[]
  readonly total: number
  readonly page: number
  readonly pageSize: number
  readonly isLoading: boolean
  readonly error: string | null
  readonly setPage: (n: number) => void
  readonly reload: () => void
  readonly create: (
    input: CreateOrganisationInput
  ) => Promise<{ ok: true } | { ok: false; field: string | null; message: string }>
  readonly update: (
    id: string,
    patch: UpdateOrganisationInput
  ) => Promise<{ ok: true } | { ok: false; field: string | null; message: string }>
  readonly remove: (id: string) => Promise<{ ok: true } | { ok: false; message: string }>
}

interface ListBody {
  readonly success: true
  readonly data: {
    readonly items: OrganisationDto[]
    readonly total: number
    readonly page: number
    readonly pageSize: number
  }
}

export function useOrganisations(): UseOrganisationsReturn {
  const { authedFetch } = useAuth()

  const paged = usePaginatedList<OrganisationDto, undefined>({
    fetcher: useCallback(
      async (page, pageSize, signal) => {
        const url = `${APIROUTES.MANAGEORGANISATIONS}?page=${page}&pageSize=${pageSize}`
        const res = await authedFetch(url, { signal })
        if (!res.ok) {
          const { message } = await parseApiError(res)
          throw new Error(message)
        }
        const body = (await res.json()) as ListBody
        return { items: body.data.items, total: body.data.total, extras: undefined }
      },
      [authedFetch]
    )
  })

  const create = useCallback(
    async (input: CreateOrganisationInput) => {
      const r = await mutateJson<OrganisationDto>(
        authedFetch,
        APIROUTES.MANAGEORGANISATIONS,
        'POST',
        input
      )
      if (!r.ok) return r
      if (paged.page === 1) paged.insertItem(r.data)
      else paged.reload()
      return { ok: true as const }
    },
    [authedFetch, paged]
  )

  const update = useCallback(
    async (id: string, patch: UpdateOrganisationInput) => {
      const r = await mutateJson<OrganisationDto>(
        authedFetch,
        fillPath(APIROUTES.MANAGEORGANISATIONITEM, { id }),
        'PATCH',
        patch
      )
      if (!r.ok) return r
      paged.updateItem((it) => it.id === id, r.data)
      return { ok: true as const }
    },
    [authedFetch, paged]
  )

  const remove = useCallback(
    async (id: string) => {
      const r = await mutateJson<{ ok: true }>(
        authedFetch,
        fillPath(APIROUTES.MANAGEORGANISATIONITEM, { id }),
        'DELETE'
      )
      if (!r.ok) return { ok: false as const, message: r.message }
      paged.removeItem((it) => it.id === id)
      return { ok: true as const }
    },
    [authedFetch, paged]
  )

  return {
    items: paged.items,
    total: paged.total,
    page: paged.page,
    pageSize: paged.pageSize,
    isLoading: paged.isLoading,
    error: paged.error,
    setPage: paged.setPage,
    reload: paged.reload,
    create,
    update,
    remove
  }
}
